import React, { useState, useEffect, Suspense } from "react";
import axios from "axios";
import config from "../../config.json";
import {
  CircularProgress,
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import SubTitle from "../../components/common/SubTitle";
import Cards from "../../components/common/Cards";
import SearchFilter from "../../components/common/SearchFilter";
import DynamicTable from "../../components/common/DynamicTable";
import Pagination from "../../components/common/Pagination";
import sortByParam from "../../hooks/SortByParam";
import searchInTheTable from "../../hooks/SarchInTable";
import DeleteModal from "../common/DeleteModal";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import Assign_Icon from "../../images/table/Track_icon.svg";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "10px",
  maxHeight: "90vh",
  overflow: "auto",
};

const DriversMain = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [sortParam, setSortParam] = useState("DriverName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [allVehicles, setAllVehicles] = useState([]);
  const [filteredDriversData, setFilteredDriversData] = useState([]);
  const [filters, setFilters] = useState({});

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  // Assign Vehicle modal state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState("");

  // Headers for the table - using strings for label instead of objects directly
  // const headers = [
  //     'Driver ID',
  //     'Driver Name',
  //     'Email',
  //     'Vehicle',
  //     'DL Number',
  //     'Phone',
  //     'Actions'
  // ];

  const headers = [
    { key: "Driver ID", value: "Driver ID" },
    { key: "Driver Name", value: "Driver Name" },
    { key: "Email", value: "Email" },
    { key: "Vehicle", value: "Vehicle" },
    { key: "DL Number", value: "DL Number" },
    { key: "Phone", value: "Phone" },
    { key: ACTIONS_COLUMN_ID, value: "Actions" },
  ];

  // Custom actions for the table
  const customActions = [
    { id: "update", label: "Update", icon: Update_Icon },
    { id: "assign", label: "Assign Vehicle", icon: Assign_Icon },
    { id: "delete", label: "Delete", icon: Delete_Icon, className: "delete" },
  ];

  const getAllDrivers = async () => {
    try {
      // Get the organization ID from local storage
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/driver/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        }
      );

      console.log("Driver data response:", response.data);
      return response.data || []; // Ensure it returns an array
    } catch (error) {
      console.error("Error while fetching the data: ", error);
      throw error;
    }
  };

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      const imageDataUrl = URL.createObjectURL(blob);
      return imageDataUrl;
    } catch (error) {
      console.error("Error while fetching image:", error);
      return null;
    }
  };

  const transformDriverData = async (data) => {
    try {
      const drivers = data;
      const transformedData = [];
      for (const driver of drivers) {
        const photoDocument = driver.documents?.find(
          (doc) => doc.name === "photo"
        );
        const photoFileName = photoDocument ? photoDocument.location : null;

        let imageUrl = null;
        if (photoFileName) {
          imageUrl = `${config.baseURL}/upload/image?fileName=${photoFileName}`;
          const imageData = await fetchImage(imageUrl);
          imageUrl = imageData;
        }

        transformedData.push({
          id: driver.id,
          DriverName: `${driver.first_name} ${driver.last_name}`,
          driver_id: driver.driver_id,
          vehicle: driver.vehicle ? driver.vehicle.device_id : "Not Assigned",
          vehicle_id: driver.vehicle ? driver.vehicle.id : null,
          DL_number: driver.driving_license_number,
          image_url: imageUrl,
          email: driver.email,
          phone: driver.phone_number,
        });
      }
      return transformedData;
    } catch (error) {
      console.error("Error while transforming the data:", error);
      return [];
    }
  };

  // Handle adding a new driver
  const handleAddDriver = () => {
    navigate("/driver/add");
  };

  // Handle search input changes
  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleActionClick = (actionId, rowIndex) => {
    // Get the action object based on actionId
    const action = customActions.find((action) => action.id === actionId);

    // Get the item data at the specified rowIndex
    const item = formatDataForTable(filteredDriversData)[rowIndex];

    console.log("Action clicked:", actionId, rowIndex, "Item:", item);

    if (!action || !item) {
      console.error("Action or item not found:", actionId, rowIndex);
      return;
    }

    switch (actionId) {
      case "update":
        // Navigate to edit page with driver data
        navigate(`/driver/add?id=${item.originalData.id}`,
          {
            state: { rowData: item.originalData }
          });
        break;
      case "assign":
        setSelectedDriver(item.originalData);
        setSelectedVehicle(item.originalData.vehicle_id || "");
        setIsAssignModalOpen(true);
        break;
      case "delete":
        setDriverToDelete(item.originalData);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDriverToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (driverToDelete) {
      try {
        // Using the correct API endpoint and payload structure based on controller code
        await axios.delete(`${config.baseURL}/driver`, {
          data: { id: driverToDelete.id },
          withCredentials: true,
        });
        fetchData();
        setIsDeleteModalOpen(false);
        setDriverToDelete(null);
      } catch (error) {
        console.error("Error deleting driver:", error);
        // alert('Failed to delete driver');
      }
    }
  };

  // Assign Vehicle modal handlers
  const handleAssignCancel = () => {
    setIsAssignModalOpen(false);
    setSelectedDriver(null);
    setSelectedVehicle("");
  };

  const handleAssignConfirm = async () => {
    if (selectedDriver && selectedVehicle) {
      try {
        // Using the correct API endpoint and payload structure based on controller code
        await axios.put(
          `${config.baseURL}/driver/vehicle`,
          {
            driverId: selectedDriver.id,
            vehicleId: selectedVehicle,
          },
          {
            withCredentials: true,
          }
        );
        fetchData();
        setIsAssignModalOpen(false);
        setSelectedDriver(null);
        setSelectedVehicle("");
      } catch (error) {
        console.error("Error assigning vehicle:", error);
        // alert('Failed to assign vehicle');
      }
    }
  };

  // Vehicle selection handler
  const handleVehicleChange = (e) => {
    setSelectedVehicle(e.target.value);
  };

  // Format data for table display - ensure all values are strings or primitives
  const formatDataForTable = (data) => {
    return data.map((driver) => ({
      "Driver ID": String(driver.driver_id),
      "Driver Name": String(driver.DriverName),
      Email: String(driver.email || ""),
      Vehicle: driver.vehicle || "Not Assigned",
      "DL Number": String(driver.DL_number || ""),
      Phone: String(driver.phone || ""),
      // Keep the original data for action handling
      id: String(driver.id),
      originalData: driver,
    }));
  };

  // Fetch vehicles data
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const vehiclesResponse = await axios.get(`${config.baseURL}/vehicle/organization`, {
          withCredentials: true,
        });
        setAllVehicles(vehiclesResponse?.data || []);
        console.log("Vehicles data response:", vehiclesResponse?.data?.vehicles);
      } catch (error) {
        console.log("Error in fetching vehicles", error);
      }
    };
    fetchVehicles();
  }, []);

  // Main data fetching function
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getAllDrivers();
      const transformedData = await transformDriverData(response);
      setDriversList(transformedData);
      setDrivers(transformedData);

      // Apply initial sorting
      let sortedDrivers = sortByParam(sortParam, transformedData);
      if (sortDirection === "desc") {
        sortedDrivers = sortedDrivers.reverse();
      }
      setFilteredDriversData(sortedDrivers);
    } catch (error) {
      console.error("Error while fetching and transforming the data:", error);
      setError("Failed to fetch driver data");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fix for search functionality
  useEffect(() => {
    if (driversList.length) {
      let result = [...driversList];

      // Apply sorting
      result = sortByParam(sortParam, result);
      if (sortDirection === "desc") {
        result = result.reverse();
      }

      // Apply search - using all searchable fields
      if (searchInput && searchInput.trim() !== "") {
        // Create a searchable version of each driver with all fields as lowercase for easier matching
        result = result.filter((driver) => {
          const searchLower = searchInput.toLowerCase();
          return (
            driver.DriverName.toLowerCase().includes(searchLower) ||
            driver.email?.toLowerCase().includes(searchLower) ||
            driver.driver_id?.toString().toLowerCase().includes(searchLower) ||
            driver.vehicle?.toLowerCase().includes(searchLower) ||
            driver.DL_number?.toLowerCase().includes(searchLower) ||
            driver.phone?.toLowerCase().includes(searchLower)
          );
        });
      }

      // Apply filters if implemented
      if (Object.keys(filters).length) {
        // Implementation depends on your filter structure
        console.log("Filters:", filters);
      }

      setFilteredDriversData(result);
    }
  }, [searchInput, sortParam, sortDirection, filters, driversList]);

  // Calculate stats for the cards
  const assignedVehicles = driversList.filter(
    (driver) => driver.vehicle && driver.vehicle !== "Not Assigned"
  ).length;
  const notAssignedVehicles = driversList.length - assignedVehicles;

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <SubTitle
        title="Drivers"
        path="Dashboard > Drivers"
        add="+ Create Driver"
        addPath="/driver/add"
        onAddClick={handleAddDriver}
      />

      <Cards
        cardData={[
          {
            title: "Total Drivers",
            value: driversList.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "Assigned Vehicles",
            value: assignedVehicles,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "Not Assigned Vehicles",
            value: notAssignedVehicles,
            backgroundColor: CARD_BACKGROUND_COLORS.RED,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          searchPlaceholder="Search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense
          fallback={
            <div className="loader-container">
              <CircularProgress />
            </div>
          }
        >
          {isLoading ? (
            <div className="loader-container">
              <CircularProgress />
            </div>
          ) : (
            <DynamicTable
              headers={headers}
              data={formatDataForTable(filteredDriversData || [])}
              onActionClick={handleActionClick}
              actions={customActions}
            />
          )}
        </Suspense>
        {/* 
                <Pagination
                    totalItems={filteredDriversData?.length || 0}
                /> */}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Driver"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {driverToDelete?.DriverName || "this driver"}?{" "}
            </span>
            {t("This action cannot be undone.")}
          </>
        }
      />

      {/* Assign Vehicle Modal - Using Material UI Modal properly */}
      <Modal
        open={isAssignModalOpen}
        onClose={handleAssignCancel}
        aria-labelledby="assign-vehicle-modal-title"
        aria-describedby="assign-vehicle-modal-description"
      >
        <Box
          sx={{
            ...modalStyle,
            border: "1px solid #A5DCF3",
            boxShadow: "-2px 2px 5px 0px #00000040",
            background: "#FFFFFF",
          }}
        >
          {/* Modal Header */}
          <Box sx={{ mb: 3, textAlign: "center", position: "relative" }}>
            <Typography
              id="assign-vehicle-modal-title"
              variant="h5"
              component="h2"
              align="center"
            >
              <span style={{ color: "#414958" }}>Assign Vehicle to</span>
              <br />
              <span style={{ color: "#183059", fontWeight: 500 }}>
                {selectedDriver?.DriverName}
              </span>
            </Typography>
            <Button
              onClick={handleAssignCancel}
              sx={{
                position: "absolute",
                right: -12,
                top: -12,
                minWidth: "auto",
                p: 1,
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              &times;
            </Button>
          </Box>

          {/* Modal Body */}
          <Box sx={{ mb: 3 }} id="assign-vehicle-modal-description">
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="vehicle-select-label">Select Vehicle</InputLabel>
              <Select
                labelId="vehicle-select-label"
                id="vehicleSelect"
                value={selectedVehicle}
                onChange={handleVehicleChange}
                label="Select Vehicle"
                sx={{
                  textAlign: "center",
                  border: "1px solid #B7BFCC",
                }}
              >
                <MenuItem value="" sx={{ textAlign: "center" }}>
                  -- Select a Vehicle --
                </MenuItem>
                {allVehicles.map((vehicle) => (
                  <MenuItem
                    key={vehicle.id}
                    value={vehicle.id}
                    sx={{ textAlign: "center" }}
                  >
                    {vehicle.device_id} - {vehicle.make} {vehicle.model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDriver?.vehicle_id &&
              selectedDriver.vehicle !== "Not Assigned" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  Currently assigned to: {selectedDriver.vehicle}
                </Typography>
              )}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}
          >
            <Button
              onClick={handleAssignCancel}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                fontSize: 16,
                background: "#F2F2F3",
                border: "1px solid #414958",
                color: "#1F242E",
                "&:hover": {
                  background: "#E5E5E6",
                  border: "1px solid #414958",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignConfirm}
              disabled={!selectedVehicle}
              sx={{
                minWidth: 100,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                fontSize: 16,
                background: !selectedVehicle ? "#cccccc" : "#F9E719",
                border: "1px solid #414958",
                color: "#1F242E",
                "&:hover": {
                  background: !selectedVehicle ? "#cccccc" : "#E1D016",
                  border: "1px solid #414958",
                },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DriversMain;